import { useState } from 'react';
import { Typography } from '@material-tailwind/react';
import { PiCaretDown, PiCaretRight } from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Loading from 'src/modules/shared/components/atoms/loading';
import { motion, AnimatePresence } from 'framer-motion';
import { useScrollHint } from 'src/modules/shared/hooks/scroll/hint';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

export type ExpansionControlType =
  | 'none'
  | 'left-icon'
  | 'clickable-title'
  | 'bottom-text';

interface ExpandableSectionProps {
  title: string;
  loading?: boolean;
  children: React.ReactNode;
  className?: string;
  isInitiallyExpanded?: boolean;
  actions?: React.ReactNode;
  expansionControl?: ExpansionControlType;
  onExpandChange?: (isExpanded: boolean) => void;
  contentClassName?: string;
  titleClassName?: string;
  isContentScrollable?: boolean;
  /**
   * When true, shows a subtle scroll hint icon at the bottom of the content
   * whenever the content is vertically scrollable and the user has not reached
   * the bottom yet. Helps communicate that more content is available.
   */
  showScrollHint?: boolean;
  fillHeight?: boolean;
  /**
   * Optional controlled expanded state. When provided, the component becomes
   * controlled and will not manage its own expanded state internally.
   */
  expanded?: boolean;
  compactHeaderOnMobile?: boolean;
}

const getExpandableSectionClasses = ({
  compactHeaderOnMobile,
  fillHeight,
  className,
  titleClassName,
}: {
  compactHeaderOnMobile: boolean;
  fillHeight: boolean;
  className: string;
  titleClassName: string;
}) => {
  const rootClasses = [
    compactHeaderOnMobile
      ? fillHeight
        ? 'flex h-full min-h-0 flex-col gap-1.5 py-0 sm:gap-2 sm:py-0'
        : 'space-y-1.5 py-0 sm:space-y-2 sm:py-0'
      : fillHeight
        ? 'flex h-full min-h-0 flex-col gap-3 py-4'
        : 'space-y-3 py-4',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  const headerClasses = compactHeaderOnMobile
    ? 'flex items-center justify-between px-3 py-1.5 min-w-0 gap-2 sm:px-4 sm:py-2'
    : 'flex items-center justify-between px-4 min-w-0 gap-2';
  const titleClasses = compactHeaderOnMobile
    ? `text-sm sm:text-md font-medium transition-colors group-hover:text-primary-600 select-none ${titleClassName}`
    : `text-md font-medium transition-colors group-hover:text-primary-600 select-none ${titleClassName}`;
  const contentPaddingClasses = compactHeaderOnMobile
    ? `flex flex-col space-y-1.5 py-0 px-3 sm:space-y-2 sm:px-4 ${fillHeight ? 'min-h-full' : ''}`
    : `flex flex-col space-y-2 py-1 px-4 ${fillHeight ? 'min-h-full' : ''}`;

  return { rootClasses, headerClasses, titleClasses, contentPaddingClasses };
};

const ExpandableSectionTitleButton = ({
  isExpanded,
  onToggle,
  title,
  titleClasses,
  toggleTitle,
}: {
  isExpanded: boolean;
  onToggle: () => void;
  title: string;
  titleClasses: string;
  toggleTitle: string;
}) => (
  <button
    type="button"
    onClick={onToggle}
    className="flex items-center cursor-pointer group"
    aria-expanded={isExpanded}
    title={toggleTitle}
  >
    <div className="mr-2 transition-colors group-hover:text-primary-600">
      {isExpanded ? (
        <PiCaretDown className="h-4 w-4" />
      ) : (
        <PiCaretRight className="h-4 w-4" />
      )}
    </div>
    <Typography className={titleClasses}>{title}</Typography>
  </button>
);

const ExpandableSectionScrollHint = ({
  hintOpacity,
  title,
}: {
  hintOpacity: number;
  title: string;
}) => (
  <div
    className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center pb-1"
    aria-hidden="true"
    title={title}
    style={{ opacity: hintOpacity }}
  >
    <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white via-white/95 to-transparent" />
    <PiCaretDown className="relative z-10 h-4 w-4 text-primary-300" />
  </div>
);

export const ExpandableSection: React.FC<ExpandableSectionProps> = ({
  title,
  loading = false,
  children,
  className = '',
  isInitiallyExpanded = true,
  actions,
  expansionControl = 'left-icon',
  onExpandChange,
  contentClassName = 'max-h-[10rem]',
  titleClassName = '',
  isContentScrollable = true,
  showScrollHint = true,
  fillHeight = false,
  expanded,
  compactHeaderOnMobile = false,
}) => {
  const { t } = useI18n();
  const isControlled = typeof expanded === 'boolean';
  const [internalExpanded, setInternalExpanded] = useState(isInitiallyExpanded);
  const isExpanded = isControlled ? expanded : internalExpanded;
  const {
    containerRef: scrollRef,
    isInitialized,
    isScrollable,
    isAtBottom,
    hintOpacity,
  } = useScrollHint<HTMLDivElement>({
    enabled: isContentScrollable && isExpanded,
    fadeDistancePx: 16,
    initDelayMs: 220,
  });

  const toggleExpand = () => {
    const newExpandedState = !isExpanded;
    if (!isControlled) {
      setInternalExpanded(newExpandedState);
    }
    onExpandChange?.(newExpandedState);
  };

  const getTitleContainerProps = () => {
    if (expansionControl === 'clickable-title') {
      return {
        onClick: toggleExpand,
        className: `font-medium cursor-pointer hover:text-primary-600 transition-colors select-none ${titleClassName}`,
        role: 'button',
        'aria-expanded': isExpanded,
      };
    }
    return { className: `font-medium select-none ${titleClassName}` };
  };

  const contentClasses = [
    'relative',
    fillHeight ? 'flex-1 min-h-0' : '',
    isContentScrollable ? 'overflow-hidden' : '',
    contentClassName,
    isContentScrollable ? 'overflow-y-auto' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const { rootClasses, headerClasses, titleClasses, contentPaddingClasses } =
    getExpandableSectionClasses({
      compactHeaderOnMobile,
      fillHeight,
      className,
      titleClassName,
    });

  return (
    <div className={rootClasses}>
      <div className={headerClasses}>
        <div className="flex items-center min-w-0 overflow-hidden">
          {expansionControl === 'left-icon' ? (
            <ExpandableSectionTitleButton
              isExpanded={isExpanded}
              onToggle={toggleExpand}
              title={title}
              titleClasses={titleClasses}
              toggleTitle={
                isExpanded
                  ? t('shared.expandableSection.hide')
                  : t('shared.expandableSection.show')
              }
            />
          ) : (
            <Typography {...getTitleContainerProps()}>{title}</Typography>
          )}
        </div>
        {actions && (
          <div className="flex items-center space-x-2 flex-shrink-0">
            {actions}
          </div>
        )}
      </div>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            layout
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={contentClasses}
            ref={isContentScrollable ? scrollRef : undefined}
          >
            <div className={contentPaddingClasses}>
              {loading ? (
                <div className="flex justify-center py-4">
                  <Loading size="sm" />
                </div>
              ) : (
                <>
                  {children}
                  {expansionControl === 'bottom-text' && (
                    <div className="text-center pt-2">
                      <Button
                        variant="neutral"
                        appearance="ghost"
                        size="sm"
                        onClick={toggleExpand}
                        className="font-medium text-primary-600"
                      >
                        {t('shared.expandableSection.viewLess')}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
            {showScrollHint &&
              isContentScrollable &&
              isInitialized &&
              isScrollable &&
              !isAtBottom &&
              hintOpacity > 0 && (
                <ExpandableSectionScrollHint
                  hintOpacity={hintOpacity}
                  title={t('shared.expandableSection.moreContent')}
                />
              )}
          </motion.div>
        )}
      </AnimatePresence>

      {!isExpanded && expansionControl === 'bottom-text' && (
        <div className="text-center">
          <Button
            variant="neutral"
            appearance="ghost"
            size="sm"
            onClick={toggleExpand}
            className="font-medium text-primary-600"
          >
            {t('shared.expandableSection.viewMore')}
          </Button>
        </div>
      )}
    </div>
  );
};
